import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Paper, Typography, IconButton, CircularProgress } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTheme } from "@mui/material/styles";
import { getTextColor } from '../../../../utils/colorUtils';
import { ModalStackProvider, useModalStack } from '../../../../components/Modal/ModalStackContext'; // Import useModalStack directly
import MainCard from "../../../../components/cards/MainCard";

import ContentPackCoursePreviewModal from '../../../content-packs/ContentPackCoursePreviewModal';
import { CalendarSimulationPreviewModal } from '../modals/CalendarSimulationPreview';
import SimulationCollectionPreview from '../../../sim-collections/SimulationCollectionPreview';
import ExportToPresetModal from '../modals/ExportToPresetModal';
import AddItemModal from '../modals/AddItemModal';
import EditItemModal from '../modals/EditItemModal';
import MultiItemSelectModal from '../modals/MultiItemSelectModal';
import CalendarModalRenderer from '../modals/CalendarModalRenderer'

import useCalendarData from '../../hooks/useCalendarData';
import useCalendarNavigation from '../../hooks/useCalendarNavigation';

import CalendarGrid from './CalendarGrid';
import CalendarHeader from './CalendarHeader';
import PaginationControls from './PaginationControls';

import { useGetSimulationsQuery } from "../../../../store/services/simulation-service";
import { useGetSimulationCollectionsQuery } from "../../../../store/services/simulation-service";
import { useGetCoursesQuery } from "../../../../store/services/university-service";
import { formatDate } from '../../utils/dateUtils';

const ContentDeliveryCalendarWithProvider = (props) => {
    return (
        <ModalStackProvider>
            <ContentDeliveryCalendar {...props} />
        </ModalStackProvider>
    );
};

const ContentDeliveryCalendar = ({
    mode = 'edit',
    deliveryGroupId,
    onClose,
    showNotification,
    presetData,
    startDate
}) => {
    const theme = useTheme();
    const { pushModal, popModal } = useModalStack();
    const { tenancyId, featureSwitches: featureSwitchesApiData } = useSelector((state) => state.tenancy);

    const contentTypeColors = useMemo(() => ({
        'Simulation': theme.palette.primary.main,
        'Course': theme.palette.warning.main,
        'RandomSimulation': theme.palette.primary.main, 
    }), [theme.palette.primary.main, theme.palette.warning.main]);

    // Use custom hooks
    const calendarData = useCalendarData(mode, deliveryGroupId, presetData, startDate, showNotification);
    const navigation = useCalendarNavigation(calendarData.monthsToDisplay);

    // RTK Query hooks for data
    const { data: simData } = useGetSimulationsQuery({
        perPage: 1000,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: `&published=true&tenancyId=${tenancyId}`
    });

    const { data: courseData } = useGetCoursesQuery({
        perPage: 1000,
        page: 0,
        includes: "",
        query: "",
        additionalQueryParams: `&published=true&tenancyId=${tenancyId}`
    });

    const { data: collectionData } = useGetSimulationCollectionsQuery({
        perPage: 1000,
        page: 0,
        searchTerm: "",
        tenancyId: tenancyId,
        published: true
    });

    const handleDayClickWithModal = useCallback((date, itemsAtDate = []) => {
        // Call the existing handler first
        calendarData.handleDayClick(date, itemsAtDate);

        // Now check what modal should open based on the result
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const cellDate = new Date(date);
        cellDate.setHours(0, 0, 0, 0);
        const isPastOrToday = cellDate <= today;

        if (isPastOrToday) {
            if (itemsAtDate.length >= 1) {
                pushModal({
                    type: 'multiSelect',
                    props: {
                        items: itemsAtDate,
                        date: date,
                        readOnly: true
                    }
                });
            }
        } else {
            if (calendarData.isMoveMode && calendarData.itemToMove) {
                // Move mode - no modal needed
                return;
            } else if (itemsAtDate.length >= 1) {
                pushModal({
                    type: 'multiSelect',
                    props: {
                        items: itemsAtDate,
                        date: date,
                        readOnly: false
                    }
                });
            } else {
                // Open add modal
                pushModal({
                    type: 'addItem',
                    props: { date }
                });
            }
        }
    }, [calendarData, pushModal]);

    const handleAddNewItemFromMultiSelect = useCallback((date) => {
        popModal(); // Close multi-select
        pushModal({
            type: 'addItem',
            props: { date }
        });
    }, [popModal, pushModal]);

    const handleMultiSelectItemClick = useCallback((item) => {
        calendarData.handleItemClick(item);

        popModal(); // Close multi-select

        // Small delay to prevent modal flicker
        setTimeout(() => {
            pushModal({
                type: 'editItem',
                props: { item }
            });
        }, 50);
    }, [popModal, pushModal, calendarData]);

    const handleExportToPresetWithModal = useCallback(() => {
        if (calendarData.calendarItems.length === 0) {
            showNotification('Cannot create a preset with no items', 'error');
            return;
        }
        pushModal({
            type: 'exportPreset',
            props: {}
        });
    }, [calendarData.calendarItems, showNotification, pushModal]);

    const handlePreview = useCallback(() => {
        if (!calendarData.formState.contentId) return;

        if (calendarData.formState.contentType === 'Course') {
            const course = courseData?.find(course => course.courseName === calendarData.formState.contentId);
            if (course) {
                pushModal({
                    type: 'coursePreview',
                    props: { courseId: course.id }
                });
            }
        } else if (calendarData.formState.contentType === 'Simulation') {
            if (calendarData.formState.selectedContentName === 'Random Simulation') {
                const collection = collectionData?.find(col => col.internalId === calendarData.formState.selectedCollectionId);
                if (collection) {
                    pushModal({
                        type: 'collectionPreview',
                        props: { collectionId: collection.internalId }
                    });
                }
            } else {
                const simulation = simData?.find(sim => sim.simulationName === calendarData.formState.contentId);
                if (simulation) {
                    pushModal({
                        type: 'simulationPreview',
                        props: { simulationId: simulation.id }
                    });
                }
            }
        }
    }, [calendarData.formState, courseData, simData, collectionData, pushModal]);

    const handleSaveChanges = useCallback(async () => {
        const success = await calendarData.handleSaveChanges();
        if (success) {
            onClose(true);
        }
    }, [calendarData, onClose]);

    if (calendarData.isLoadingRequest && mode === 'edit') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading calendar data...
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <MainCard
                title={
                    <CalendarHeader
                        mode={mode}
                        isMoveMode={calendarData.isMoveMode}
                        deliveryGroupName={calendarData.deliveryGroupName}
                        setDeliveryGroupName={calendarData.setDeliveryGroupName}
                        unsavedChanges={calendarData.unsavedChanges}
                        isUpdating={calendarData.isUpdating}
                        isCreating={calendarData.isCreating}
                        isAdmin={calendarData.isAdmin}
                        calendarItemsCount={calendarData.calendarItems.length}
                        attemptedSave={calendarData.attemptedSave}
                        onSave={handleSaveChanges}
                        onExportToPreset={handleExportToPresetWithModal}
                        onCancelMove={calendarData.handleCancelMoveMode}
                        onClose={onClose}
                    />
                }
                content={true}
                sx={{ bgcolor: 'background.default' }}
            >
                {/* Banner sections remain the same */}
                {mode === 'preset' && calendarData.showBanner && (
                    <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: theme.palette.primary.main, color: getTextColor(theme.palette.primary.main), display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px' }}>
                        <Box>
                            <Typography variant="h6">
                                Template Schedule Starting {new Date(startDate).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2">
                                Items from the template "{presetData?.name}" have been placed on your calendar.
                                You can add, remove, or move items before creating the final schedule.
                            </Typography>
                        </Box>
                        <IconButton size="small" sx={{ color: 'inherit' }} onClick={() => calendarData.setShowBanner(false)}>
                            <CancelIcon />
                        </IconButton>
                    </Paper>
                )}

                {mode === 'create' && calendarData.calendarItems.length === 0 && calendarData.showBanner && (
                    <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: theme.palette.primary.main, color: getTextColor(theme.palette.primary.main), display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px' }}>
                        <Box>
                            <Typography variant="h6">
                                Click on any day to add content
                            </Typography>
                            <Typography variant="body2">
                                To create a journey, add at least one content item to the calendar
                            </Typography>
                        </Box>
                        <IconButton size="small" sx={{ color: 'inherit' }} onClick={() => calendarData.setShowBanner(false)}>
                            <CancelIcon />
                        </IconButton>
                    </Paper>
                )}

                {/* Calendar Grid */}
                <Grid container>
                    <CalendarGrid
                        monthsByYear={navigation.monthsByYear}
                        itemsByDate={calendarData.itemsByDate}
                        onDayClick={handleDayClickWithModal}
                        isMoveMode={calendarData.isMoveMode}
                        contentTypeColors={contentTypeColors}
                    />
                </Grid>

                {/* Pagination Controls */}
                <PaginationControls
                    currentPage={navigation.currentPage}
                    totalPages={navigation.totalPages}
                    handlePreviousPage={navigation.handlePreviousPage}
                    handleNextPage={navigation.handleNextPage}
                    isMoveMode={calendarData.isMoveMode}
                />

                <CalendarModalRenderer
                    formState={calendarData.formState}
                    handleFormChange={calendarData.handleFormChange}
                    handleContentTypeChange={calendarData.handleContentTypeChange}
                    handleContentSelection={calendarData.handleContentSelection}
                    handleCollectionSelection={calendarData.handleCollectionSelection}
                    onAddNewItemFromMultiSelect={handleAddNewItemFromMultiSelect}
                    onMultiSelectItemClick={handleMultiSelectItemClick}
                    simulationData={simData}
                    courseData={courseData}
                    collectionData={collectionData}
                    onAdd={calendarData.handleAddItem}
                    onUpdate={calendarData.handleUpdateItem}
                    onDelete={calendarData.handleDeleteItem}
                    onEnterMoveMode={calendarData.handleEnterMoveMode}
                    onPreview={handlePreview}
                    contentTypeColors={contentTypeColors}
                    calendarItems={calendarData.calendarItems}
                    showNotification={showNotification}
                    featureSwitchesApiData={featureSwitchesApiData} 
                />
                
            </MainCard>
        </Box>
    );
};

export default ContentDeliveryCalendarWithProvider;